<template>
	<div
		:class="[
			(itemType) ? `form-item--${itemType}` + typeModifier : '',
			(type) ? `form-item--${type}` + typeModifier : '',
			(required) ? 'form-item--required' : '',
			(disabled) ? 'form-item--disabled' : '',
			(modifiers.length) ? 'form-item--' + modifiers.join(' form-item--') : '',
			(!isValid || customErrorMessage) ? 'form-item--error' : '',
			(hasFocus) ? 'form-item--hasFocus' : '',
			(hasContent) ? 'form-item--hasContent' : '',
			(hasAutoFill) ? 'form-item--hasAutoFill' : '',
		]"
		class="form-item"
	>
		<div>
			<slot name="before" />

			<label v-if="(isContentInput || isNumberInput || isEditableNumberInput || isSelect) && label"
				:for="fieldName"
				class="form-label">{{ label }}</label>

			<div
				v-if= "type == 'dropzone'"
				v-dropzone
				@dropzone_drop="onDropzoneDrop"
				@click.prevent="onDropzoneClick"
				class="dropzone"
				ref="dropzone"
			><span class="dropzone-label">{{ dropZoneLabel }}</span></div>

			<!-- Content input -->
			<input
				v-if="isTextInput"
				ref="field"
				:id="fieldId"
				:name="fieldName"
				:type="fieldType"
				:placeholder="placeholder"
				:required="required"
				:disabled="disabled"
				:autocomplete="autocomplete"
				:value="inputValue"
				@input="onInput($event)"
				@keydown.down.prevent.stop="onKeyDown($event)"
				@keydown.up.prevent.stop="onKeyUp($event)"
				@focus="onFocus"
				@blur="onBlur">


			<input
				v-if="isDateNoOverlayInput"
				:name="fieldName"
				type="text"
				ref="field"
				:placeholder="placeholder"
				:value="inputValue"
				@input="onInput($event)"
				@keydown.down.prevent.stop="onKeyDown($event)"
				@focus="onFocus"
				@blur="onBlur"
			/>

			<!-- file input -->
			<input
				v-if="isFileInput"
				ref="field"
				:id="fieldId"
				:name="fieldName"
				:type="fieldType"
				:placeholder="placeholder"
				:required="required"
				:disabled="disabled"
				:autocomplete="autocomplete"
				@change.prevent="onFileUploadFieldChange($event)">


			<!-- Checkbox - radio, checkbox, switch, toggle -->
			<input
				v-if="isCheckOrRadio"
				ref="field"
				:id="fieldId"
				:name="fieldName"
				:type="fieldType"
				:placeholder="placeholder"
				:required="required"
				:disabled="disabled"
				:autocomplete="autocomplete"
				:value="inputValue"
				:checked="inputValue"
				:indeterminate.prop="indeterminateValue"
				@change="onCheckChange($event)"
				@input="onInput">

			<!-- Textarea -->
			<textarea
				v-else-if="isTextArea"
				v-autogrow
				ref="field"
				:id="fieldId"
				:name="fieldName"
				:placeholder="placeholder"
				:required="required"
				:disabled="disabled"
				:autocomplete="autocomplete"
				:value="inputValue"
				@input="onInput($event)">
			</textarea>


			<!-- Content input -->
			<div v-if="isNumberInput || isEditableNumberInput" class="formItem--number__wrapper">
				<input
					v-if="!isEditableNumberInput"
					ref="field"
					:id="fieldId"
					:name="fieldName"
					:type="fieldType"
					:placeholder="placeholder"
					:required="required"
					:disabled="disabled"
					:min="min"
					:max="max"
					:step="step"
					:autocomplete="autocomplete"
					:value="inputValue"
					@input="onInput($event)"
				>
				<slot name="numberBetween" />
				<span class="formControls formControls--number">
					<span
						class="formControls-button formControls-button--min"
						:class="{ disabled: disableOnEdges && currentValue === min }"
						@click.prevent="onNumberMin"
					>
						<span class="minus-text">-</span>
					</span>
					<span class="formControls-value">
						<slot
							v-if="!isEditableNumberInput"
							name="value"
						>
							{{ currentValue }}
						</slot>
						<input
							v-else
							ref="field"
							type="number"
							:id="fieldId"
							:name="fieldName"
							:placeholder="placeholder"
							:required="required"
							:disabled="disabled"
							:min="min"
							:max="max"
							:autocomplete="autocomplete"
							:value="inputValue"
							@input="onInput($event)"
						>
					</span>
					<span
						class="formControls-button formControls-button--plus"
						:class="{ disabled: disableOnEdges && currentValue === max }"
						@click.prevent="onNumberPlus"
					>
						<span class="plus-text">+</span>
					</span>
				</span>
			</div>

			<!-- Select -->
			<select
				v-if="isSelect"
				ref="field"
				:name="fieldName"
				:required="required"
				:disabled="disabled"
				:autocomplete="autocomplete"
				@input="onInput($event)" style="display:block;">

				<option selected disabled>{{ placeholder }}</option>

				<slot v-for="(option, index) of options" :option="option" name="option">
					<option
						:key="index"
						:selected="option.selected || option.value === inputValue || option.title === inputValue || option === inputValue"
						:disabled="option.disabled"
						:value="option.value || option.id || option.label || option.title">
						{{ option.label || option.title || option.value || option }}
					</option>
				</slot>
			</select>

			<!-- Advanced Select -->
			<AdvancedSelect
				v-else-if="isAVSelect"
				ref="field"
				:options="options"
				:label="label"
				:showLabel="showLabel"
				:boolSelect="boolSelect"
				:placeholder="placeholder"
				:value="inputValue"
				:dropdownWidth="dropdownWidth"
				:injectInRoot="injectInRoot"
				@input="onInput($event.target.currentItem)"
				:hideOnScroll="hideOnScroll"
				:modifiers="advancedSelectModifiers"
			>
				<template v-slot:label="slotProps">
					{{ slotProps.label }}
				</template>

				<template v-slot:placeholder="slotProps">
					<slot :option="slotProps.option" name="selectPlaceholder">{{ slotProps.placeholder }}</slot>
				</template>

				<template v-slot:header="slotProps">
					<slot :option="slotProps.option" name="selectHeader">{{ slotProps.option }}</slot>
				</template>

				<template v-slot:option="slotProps">
					<slot :option="slotProps.option" name="selectOption">{{ slotProps.option }}</slot>
				</template>
			</AdvancedSelect>

			<label
				v-if="isCheckOrRadio && label && !isToggle"
				:for="fieldName"
				class="form-label"
			>
				<span v-if="typeModifier === '-custom'" class="form-item--radio__button" />
				<slot name="label">
					{{ label }}
				</slot>

				<InfoIcon
					v-if="infoIconTooltip.content"
					class="form-label-info-icon"
					:tooltip="infoIconTooltip"
				/>
			</label>

			<label
				v-if="isToggle"
				:for="fieldName"
				class="form-label"
				:class="{
					'toggle-label-left': titlePosition === 'right',
				}"
			>
				<slot v-if="titlePosition === 'left'">
					{{ label }}
				</slot>
				<span
					class="toggle"
					:class="{
						'toggle-left': titlePosition === 'right',
					}"
				/>
				<slot v-if="titlePosition === 'right'">
					{{ label }}
				</slot>
			</label>

			<br v-if="isSwitch">
			<span v-if="isSwitch" class="form-switch" @click.prevent="$refs.field.click()">
				<span class="form-switchOption">{{ trueLabel }}</span>
				<span class="form-switchOption">{{ falseLabel }}</span>
			</span>
			<!-- datepicker -->
			<div class="datePicker"  v-if="type === 'date'">
				<div class="datepicker-container inline-with-input">
					<input
						id="datepicker-inline-trigger"
						:value="inputValue"
						type="text"
						placeholder="Select date"
					>
					<airbnb-style-datepicker
						:trigger-element-id="'datepicker-inline-trigger'"
						:mode="'single'"
						:inline="true"
						:fullscreen-mobile="false"
						:date-one="currentValue"
						@date-one-selected="onDatePickerSelected"
						:months-to-show="1"
						:show-action-buttons="true"
					/>
				</div>
			</div>
		</div>
		<!-- Errormessage -->
		<div v-if="description && description.length" class="description description--formItem form-description">{{ description }}</div>
		<div
			v-if="!isValid || customErrorMessage"
			class="form-error"
			:class="{ 'box-error': errorVariant === errorVariants.BOX }"
		>
			<slot name="errorMessage">
				<div v-html="customErrorMessage || errorMessage" />
			</slot>
		</div>
		<InfoBox  v-if="infoBoxText" :withIcon="true">
			{{ infoBoxText }}
		</InfoBox>
		<slot name="after" />
	</div>
</template>

<script src="./FormItem.js"></script>
